import { apiRequest } from '../../shared/api/client';

/**
 * Lots — mirrors Invetrak.Api.Endpoints.LotEndpoints, over tenant scripts 041 and 082.
 *
 *   GET  /api/v1/lots[?itemId=&search=&expiringBefore=&onlyWithStock=&pageSize=&pageNumber=]  lots:read
 *   GET  /api/v1/lots/{id}                                                                   lots:read
 *   POST /api/v1/lots                                                                        lots:register
 *   PUT  /api/v1/lots/{id}                                                                   lots:register
 *
 * **This is not a second way to register a lot in passing.** The scanner already does that
 * inside the receipt and adjustment paths and it stays there. POST here is the deliberate act:
 * setting a lot up before the goods arrive.
 *
 * **A lot belongs to an item, not a warehouse**, but `quantityOnHand` is the quantity in the
 * session's warehouse — "how much is here" is what somebody standing on the floor is asking.
 */

const COOKIE_INIT: RequestInit = { credentials: 'include' };

export interface LotDto {
  lotId: string;
  itemId: string;
  itemNumber: string;
  itemDescription: string | null;
  stockingUomCode: string;
  lotNumber: string;
  /** What decides which lot is picked first. Never null. */
  fifoDateUtc: string;
  expirationDateUtc: string | null;
  /** The supplier's own number for the same physical lot. */
  supplierLotNumber: string | null;
  createdAtUtc: string;
  /** In the session's warehouse. */
  quantityOnHand: number;
  /**
   * Whether anything has ever moved against it — the difference between a lot that is merely
   * empty and one that was never used.
   */
  hasMovements: boolean;
  isExpired: boolean;
}

export interface LotPageDto {
  rows: LotDto[];
  totalCount: number;
}

export interface LotFilters {
  itemId?: string | null;
  search?: string | null;
  expiringBefore?: string | null;
  onlyWithStock?: boolean;
  pageSize?: number;
  pageNumber?: number;
}

export interface RegisterLotBody {
  itemId: string;
  lotNumber: string;
  fifoDateUtc?: string | null;
  expirationDateUtc?: string | null;
  supplierLotNumber?: string | null;
}

/**
 * A correction.
 *
 * No item and no lot number: they are the lot's identity, and every ledger row, pick line and
 * packing list that names this lot points at it. Correcting a lot number is a new lot and a
 * transfer, not an edit that rewrites what a dozen documents say happened.
 */
export interface UpdateLotBody {
  fifoDateUtc: string;
  expirationDateUtc?: string | null;
  supplierLotNumber?: string | null;
}

export async function listLots(filters: LotFilters = {}, signal?: AbortSignal): Promise<LotPageDto> {
  const q = new URLSearchParams();
  if (filters.itemId) q.set('itemId', filters.itemId);
  if (filters.search) q.set('search', filters.search);
  if (filters.expiringBefore) q.set('expiringBefore', filters.expiringBefore);
  if (filters.onlyWithStock) q.set('onlyWithStock', 'true');
  if (filters.pageSize) q.set('pageSize', String(filters.pageSize));
  if (filters.pageNumber) q.set('pageNumber', String(filters.pageNumber));

  const query = q.toString();
  const { data } = await apiRequest<LotPageDto>(`/v1/lots${query ? `?${query}` : ''}`, {
    method: 'GET', signal, ...COOKIE_INIT,
  });
  return data;
}

export async function getLot(lotId: string, signal?: AbortSignal): Promise<LotDto> {
  const { data } = await apiRequest<LotDto>(`/v1/lots/${encodeURIComponent(lotId)}`, {
    method: 'GET', signal, ...COOKIE_INIT,
  });
  return data;
}

export async function registerLot(body: RegisterLotBody): Promise<LotDto> {
  const { data } = await apiRequest<LotDto>('/v1/lots', {
    method: 'POST', body, ...COOKIE_INIT,
  });
  return data;
}

export async function updateLot(lotId: string, body: UpdateLotBody): Promise<LotDto> {
  const { data } = await apiRequest<LotDto>(`/v1/lots/${encodeURIComponent(lotId)}`, {
    method: 'PUT', body, ...COOKIE_INIT,
  });
  return data;
}
